// Utility functions for user role management and permissions

/**
 * Check if user has a specific role
 * @param {Object} user - User object with role property
 * @param {string} role - Role to check against
 * @returns {boolean} - True if user has the role
 */
export const hasRole = (user, role) => {
  if (!user || !user.role) return false;
  return user.role === role;
};

/**
 * Check if user is admin (LOB head)
 * @param {Object} user - User object
 * @returns {boolean} - True if user is admin
 */
export const isAdmin = (user) => {
  return hasRole(user, "admin");
};

/**
 * Check if user is manager
 * @param {Object} user - User object
 * @returns {boolean} - True if user is manager
 */
export const isManager = (user) => {
  return hasRole(user, "manager");
};

/**
 * Check if user is OPS (previously agent)
 * @param {Object} user - User object
 * @returns {boolean} - True if user is OPS
 */
export const isOPS = (user) => {
  return hasRole(user, "ops");
};

/**
 * Check if user is client
 * @param {Object} user - User object
 * @returns {boolean} - True if user is client
 */
export const isClient = (user) => {
  return hasRole(user, "client");
};

/**
 * Check if user has permission to access a feature
 * @param {Object} user - User object with permissions array
 * @param {string} permission - Permission to check
 * @returns {boolean} - True if user has permission
 */
export const hasPermission = (user, permission) => {
  if (!user || !user.permissions) return false;
  return user.permissions.includes(permission);
};

/**
 * Get dashboard type based on user role
 * @param {Object} user - User object
 * @returns {string} - Dashboard type (lob, ops, manager, client)
 */
export const getDashboardType = (user) => {
  if (isAdmin(user)) return "lob";
  if (isManager(user)) return "manager";
  if (isOPS(user)) return "ops";
  if (isClient(user)) return "client";
  return "default";
};

// Define route access permissions
const routePermissions = {
  "/dashboard": ["admin", "ops"],
  "/user-management": ["admin", "ops"],
  "/claims": ["admin", "ops"],
  "/bookings": ["admin", "ops"],
  "/vouchers": ["admin", "ops"],
  "/change-password": ["admin", "ops"],
  "/cd-balance-claims": ["admin", "ops"],
  "/cd-balance-premium": ["admin", "ops"],
  "/client-management": ["admin"],
  "/change-client": ["admin", "ops"],
};

/**
 * Check if user can access a specific page/route
 * @param {Object} user - User object
 * @param {string} routePath - Route path to check
 * @returns {boolean} - True if user can access the route
 */
export const canAccessRoute = (user, routePath) => {
  if (!user || !user.role) return false;

  const allowedRoles = routePermissions[routePath];
  return allowedRoles ? allowedRoles.includes(user.role?.toLowerCase()) : false;
};

/**
 * Filter sidebar menu items based on user role
 * @param {Array} menuItems - All menu items
 * @param {Object} user - User object
 * @returns {Array} - Filtered menu items based on user role
 */
export const filterMenuItemsByRole = (menuItems, user) => {
  if (!user || !user.role) return [];

  return menuItems.filter((item) => {
    const allowedRoles = routePermissions[item.path];
    return allowedRoles
      ? allowedRoles.includes(user.role?.toLowerCase())
      : false;
  });
};
